import Vue from 'vue'
import applogger from '@applogger'

const state = () => ({
  filecache: {},
  filemetadata: {},
  filestores: null
})

const queryStringURL = (url, qs) => {
  qs = Object.entries(qs).map(([k, v]) => (v !== null) ? `${k}=${v}` : null).filter(x => x).join('&')
  return url + ((qs) ? '?' + qs : '')
}

const mutations = {
  REGISTER_FILESTORES(state, data) {
    if (state.filestores === null) {
      state.filestores = data
    }
  },
  SET_FILECACHE_ITEM: function(state, {filekey, file}) {
    Vue.set(state.filecache, filekey, file)
  },
  STORE_FILE_METADATA: function(state, {filekey, metadata}) {
    Vue.set(state.filemetadata, filekey, metadata)
  }
}

const actions = {
  init: function({commit, state, rootGetters}) {
    applogger.debug('Init Filestores')
    if (state.filestores !== null) {
      return
    }

    return this.dispatch('loadItemFromRemote', { url: `/vuejs/filestores`, config: rootGetters.AuthorizedRequestConfig() })
      .then(filestores => {
        commit('REGISTER_FILESTORES', filestores.data)
      })
      .catch(err => {
        let statusMessage = {
          type: 'error',
          title: `Error loading Filestore Information`,
          text: `
            <p>There was an error encountered loading the Filestore Details.</p>
            <p>Please reload the page and if the problem persists please contact the
            site admin.</p>
          <p>Error: ${err.message}</p>
          `,
          notification: true,
          showClose: true
        }
        this.dispatch('reportError', statusMessage)
        applogger.debug('Error loading filestores: ', err)
      })
  },

  fuswsrequest: function({getters, rootGetters}, payload) {
    const fuswsurl = (op, file) => {
      let queryString
      switch (op) {
      case 'metadata':
        return `/api/v1/local/${file.filepath}`

      case 'presigned-url':
        queryString = {
          filename: file.filepath,
          preset: (file.preset !== undefined && file.preset !== '__original') ? file.preset : null
        }
        return queryStringURL(`/api/v1/local/presigned-url`, queryString)
      }
    }
    let filestore = getters['filestoreInfo'](payload.filestorename)
    const url = filestore.endpoint + fuswsurl(payload.op, payload)
    const config = {
      method: 'GET',
      headers: {
        'X-Filestore-ID': filestore.filestoreID
      }
    }
    const jwt = rootGetters['user/verifiedJWT']
    if (jwt) {
      config.headers.Authorization = `Bearer ${jwt}`
    }
    return new Promise((resolve, reject) => {
      fetch(url, config)
        .then(resp => {
          if (resp.ok) {
            resolve(resp.json())
          }
          reject(new Error(`FusWS::fuswsRequest Error - ${resp.status} - ${resp.statusText}`))
        })
        .catch(_err => {
          resolve(null)
        })
    })
      .then(resp => {
        return resp.data !== undefined ? resp.data : resp
      })
  },

  getFileMetaData: function({ state, commit, dispatch, getters }, payload) {
    if (payload.fid !== undefined || !payload.filepath || !payload.filestorename) {
      return null
    }
    else {
      let meta = getters.fileMetadata(payload)
      if (meta) { // TODO: add expiry.
        return Promise.resolve(meta)
      }
      return dispatch('fuswsrequest', {op: 'metadata', ...payload})
        .then(data => {
          let metadata = {
            filekey: generateFileCacheKey(state, payload),
            metadata: {
              filename: data.name,
              filesize: data.size,
              filemime: data.mimeType
            }
          }
          commit('STORE_FILE_METADATA', metadata)
        })
        .catch(err => {
          throw new Error(`FusWS::GetFileMetaData Error - ${err.message}.`)
        })
    }
  },

  getFileURLBulk: function({commit, getters, rootGetters, state}, payload) {
    const getStoredFileURL = function(filedata) {
      let file = state.filecache[generateFileCacheKey(state, filedata)]
      if (file && (file.token == null || file.expires > 0) && file.expires > (new Date().getUTCMilliseconds() / 1000)) {
        let filestore = state.filestores[payload.filestorename]
        if (filestore) {
          return filestore.endpoint + file.url
        }
      }
    }
    let filestorename = Object.prototype.hasOwnProperty.call(payload, 'filestorename') ? payload.filestorename : 'website'

    // Generate the list of filename that need to be queried from the server.
    let processinglist = payload.files.filter(x => x).reduce((acc, filedata) => {
      // Request for file has already been found in the payload
      if (acc.includes(filedata.filepath)) {
        return acc
      }
      // Request is for a drupal file - this is not requested from fusws
      if (Object.prototype.hasOwnProperty.call(filedata, 'fid')) {
        return acc
      }
      // If we are requesting a to refresh the files or the file presigned url has already been retreived.
      if (!payload.refresh && getStoredFileURL({ filestorename: filestorename, ...filedata })) {
        return acc
      }
      // add to the list of file that need to be retreived.
      return [ ...acc, filedata.filepath ]
    }, [])

    // Make the request to retreive teh bulk list of file pre-signed urls.
    let filestore = getters['filestoreInfo'](filestorename)
    const url = `${filestore.endpoint}/api/v1/local/presigned-url`
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'appication/json',
        'X-Filestore-ID': filestore.filestoreID
      },
      body: JSON.stringify(processinglist.filter(x => x))
    }
    const jwt = rootGetters['user/verifiedJWT']
    if (jwt) {
      config.headers.Authorization = `Bearer ${jwt}`
    }
    return new Promise((resolve, reject) => {
      let timeout
      let didTimeOut = false

      timeout = setTimeout(() => {
        didTimeOut = true
        reject(new Error('Request Timeout'))
      }, 2500)
      fetch(url, config)
        .then(resp => {
          if (resp.ok) {
            // Clear the timeout as cleanup
            clearTimeout(timeout)
            if (!didTimeOut) {
              resolve(resp.json())
            }
          }
          throw new Error(`FusWS::getFileURLBulk Error - ${resp.status} - ${resp.statusText}`)
        })
        .catch(_err => {
          resolve(null)
        })
    })

      .then(processedfiles => {
        // Process the request list and for any files presigned urls that were retreived store them in the file cache.

        return payload.files.reduce((acc, filedata) => {
          // The filelist only contains filename - no preset provided
          if (typeof filedata === 'string') {
            filedata = {
              name: filedata
            }
          }
          let data
          let preset = (Object.prototype.hasOwnProperty.call(filedata, 'preset') && filedata.preset !== '__original') ? filedata.preset : (Object.prototype.hasOwnProperty.call(payload, 'preset') && payload.preset !== '__original') ? payload.preset : null

          // Find if the file was retrived in the request of whether it has already been cached
          if (Object.prototype.hasOwnProperty.call(processedfiles, filedata.filepath)) {
            data = processedfiles[filedata.filepath]
            let qs = {
              preset: preset,
              t: (data.modified) ? new Date(data.modified).getTime() : ''
            }

            let newFileData = {
              ...data,
              url: queryStringURL(data.url, qs)
            }
            commit('SET_FILECACHE_ITEM', {
              filekey: generateFileCacheKey(state, { preset, ...newFileData, filepath: data.filename, filestorename: filestorename }),
              file: newFileData
            })
          }
          else {
            data = getStoredFileURL({ ...filedata, filestorename: filestorename })
          }
          if (data) {
            if (typeof filedata.callback === 'function') {
              process.nextTick(() => filedata.callback(`${filestore.endpoint}${data.url}`))
            }
            if (typeof payload.callback === 'function') {
              process.nextTick(() => payload.callback(filedata.name, preset`${filestore.endpoint}${data.url}`))
            }
            if (filedata.filepath && data.url) {
              return { ...acc, [filedata.filepath]: `${filestore.endpoint}${data.url}` }
            }
            return acc
          }
          return acc
        }, {})
      })
      .catch(err => {
        applogger.error(`FusWS Bulk PresignedURL Request Error - ${err.message}.`)
        return Promise.resolve(null)
      })
  },

  getFileURL: function({commit, rootGetters, getters, dispatch, state}, payload) {
    if (payload.fid !== undefined) {
      let servicehost = rootGetters.registry.getServiceEndpoint('datastore')
      return Promise.resolve(`${servicehost}/${payload.filepath}`)
    }
    else {
      if (!payload.filepath || !payload.filestorename) {
        return Promise.resolve(null)
      }
      if (!payload.refresh) {
        let file = state.filecache[generateFileCacheKey(state, payload)]
        if (file && (file.token == null || file.expires > 0) && file.expires > (new Date().getUTCMilliseconds() / 1000)) {
          let filestore = state.filestores[payload.filestorename]
          if (filestore) {
            return Promise.resolve(filestore.endpoint + file.url)
          }
        }
      }
      return dispatch('fuswsrequest', {op: 'presigned-url', ...payload})
        .then(data => {
          let qs = {
            preset: (payload.preset && payload.preset !== '__original') ? payload.preset : null,
            t: (data.modified) ? new Date(data.modified).getTime() : ''
          }
          data.url = queryStringURL(data.url, qs)
          commit('SET_FILECACHE_ITEM', {
            filekey: generateFileCacheKey(state, payload),
            file: data
          })
          let filestore = getters.filestoreInfo(payload.filestorename)
          if (typeof payload.callback === 'function') {
            process.nextTick(() => payload.callback(`${filestore.endpoint}${data.url}`))
          }
          return `${filestore.endpoint}${data.url}`
        })
        .catch(err => {
          applogger.error(`FusWS Request Error - ${err.message}.`)
          if (typeof payload.callback === 'function') {
            process.nextTick(() => payload.callback(null))
          }
          return Promise.resolve(null)
        })
    }
  }
}

const generateFileCacheKey = (state, file) => {
  let filestore = state.filestores[file.filestorename]
  if (!filestore) {
    throw new Error('Invalid filestore name')
  }
  return [
    filestore.endpoint,
    filestore.filestoreID,
    file.filepath,
    ((file.preset) ? file.preset : '__original')
  ]
    .join('::')
}

const getters = {
  filestoreInfo: function(state) {
    return (name) => {
      let filestore = state.filestores[name]
      if (!filestore) {
        throw new Error('Invalid filestore name')
      }
      return filestore
    }
  },
  fileMetadata: function(state) {
    return (file) => {
      if (!file.filepath || !file.filestorename) {
        return null
      }
      try {
        return state.filemetadata[generateFileCacheKey(state, file)]
      }
      catch (e) {
        return null
      }
    }
  },

  fileCachedImageURL: function(state, _getters, _rootState, rootGetters) {
    return (file) => {
      if (!file || !file.filepath || !file.filestorename) {
        return null
      }
      if (file.fid !== undefined) {
        let servicehost = rootGetters.getServiceEndpoint('datastore')
        return `${servicehost}/${file.filepath}`
      }
      try {
        let previewURL = state.filecache[generateFileCacheKey(state, file)]
        let filestore = state.filestores[file.filestorename]
        return (filestore && previewURL) ? `${filestore.endpoint}${previewURL.url}` : null
      }
      catch (e) {
        return null
      }
    }
  },
  presetsForCategory: function() {
    return category => {
      if (category === 'features') {
        return {
          featurepagefeature: 'Feature Page Feature',
          featurepagehighlight: 'Feature Page Highlight'
        }
      }
      if (category === 'gallery') {
        return {
          __original: 'Original',
          banner: 'Banner Image',
          'carousel-image': 'Carousel Image 2K Screen Resolution',
          'grid-image-full': 'Full-sized Grid Photo Card Background Image'

        }
      }
      return {}
    }
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
